import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { extractApiErrorMessage } from "../../../utils/apiError";

interface ApplicationsState {
  applications: any[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
}

interface ApplicationDetailsState {
  data: any;
  loading: boolean;
  error: string | null;
}

interface ManagerPortalState {
  pendingApplications: ApplicationsState;
  pastApplications: ApplicationsState;
  otherApplications: ApplicationsState;
  applicationDetails: ApplicationDetailsState;
}

const commonInitialState: ApplicationsState = {
  applications: [],
  loading: false,
  error: null,
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  totalPages: 0,
};

const applicationDetailsInitialState: ApplicationDetailsState = {
  data: {},
  loading: false,
  error: null,
};

const initialState: ManagerPortalState = {
  pendingApplications: commonInitialState,
  pastApplications: commonInitialState,
  otherApplications: commonInitialState,
  applicationDetails: applicationDetailsInitialState,
};

// Fetch pending applications (isSubmitted: true)
export const fetchPendingApplications = createAsyncThunk<
  any,
  { page?: number; per_page?: number },
  { rejectValue: string }
>("tuition/fetchPendingApplications", async (params, { rejectWithValue }) => {
  try {
    const page = params.page ?? 1;
    const per_page = params.per_page ?? 10;
    const url = `${endpoints.tuition.tuitionManagerPortal.managerApplicationsList}?per_page=${per_page}&page=${page}&isSubmitted=true`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(extractApiErrorMessage(error, "Failed to fetch pending applications"));
  }
});

// Fetch past applications (isSubmitted: false)
export const fetchPastApplications = createAsyncThunk<
  any,
  { page?: number; per_page?: number },
  { rejectValue: string }
>("tuition/fetchPastApplications", async (params, { rejectWithValue }) => {
  try {
    const page = params.page ?? 1;
    const per_page = params.per_page ?? 10;
    const url = `${endpoints.tuition.tuitionManagerPortal.managerApplicationsList}?per_page=${per_page}&page=${page}&isSubmitted=false`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(extractApiErrorMessage(error, "Failed to fetch past applications"));
  }
});

// Fetch other applications with x-emp-nuid header
export const fetchOtherApplications = createAsyncThunk<
  any,
  { page?: number; per_page?: number; empNuid: string },
  { rejectValue: string }
>("tuition/fetchOtherApplications", async (params, { rejectWithValue }) => {
  try {
    const page = params.page ?? 1;
    const per_page = params.per_page ?? 10;
    const { empNuid } = params;
    const url = `${endpoints.tuition.tuitionManagerPortal.managerOtherApplicationsList}?per_page=${per_page}&page=${page}`;
    const response = await api.get(url, {
      headers: {
        "x-emp-nuid": empNuid,
      },
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(extractApiErrorMessage(error, "Failed to fetch other applications"));
  }
});

// Fetch application details with x-emp-nuid header
export const fetchApplicationAllDetails = createAsyncThunk<
  any,
  { applicationId: string; empNuid: string },
  { rejectValue: string }
>("tuition/fetchApplicationAllDetails", async (params, { rejectWithValue }) => {
  try {
    const { applicationId, empNuid } = params;
    const url =
      endpoints.tuition.tuitionManagerPortal.applicationAllDetals(
        applicationId
      );
    const response = await api.get(url, {
      headers: {
        "x-emp-nuid": empNuid,
      },
    });
    const mappedData = {
      ...response.data,
      empShift: response.data.shiftStdHours ?? "",
    };
    return mappedData;
  } catch (error: any) {
    return rejectWithValue(extractApiErrorMessage(error,"Failed to fetch application details"));  
  }
});

// Update application status with patch request
export const updateApplicationStatus = createAsyncThunk<
  any,
  {
    status: string;
    applicationId: string;
    comments?: string;
    empNuid?: string;
  },
  { rejectValue: string }
>("tuition/updateApplicationStatus", async (params, { rejectWithValue }) => {
  try {
    const { status, applicationId, comments, empNuid } = params;
    const url = endpoints.tuition.tuitionManagerPortal.updateApplicationStatus;
    const payload = {
      status,
      applicationId,
      comments,
    };
    const response = await api.patch(url, payload, {
      headers: {
        "x-emp-nuid": empNuid,
      },
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(extractApiErrorMessage(error, "Failed to update application status"));
  }
});

// Pending Applications Slice
const tuitionManagerPortalSlice = createSlice({
  name: "tuitionManagerPortal",
  initialState,
  reducers: {
    setPendingCurrentPage(state, action: PayloadAction<number>) {
      state.pendingApplications.currentPage = action.payload;
    },
    setPendingPerPage(state, action: PayloadAction<number>) {
      state.pendingApplications.perPage = action.payload;
    },
    clearPendingApplications(state) {
      state.pendingApplications.applications = [];
      state.pendingApplications.totalItems = 0;
      state.pendingApplications.error = null;
    },
    removePendingApplication(state, action: PayloadAction<string>) {
      const appId = action.payload;
      state.pendingApplications.applications =
        state.pendingApplications.applications.filter(
          (app: any) => String(app.id) !== appId
        );
      state.pendingApplications.totalItems = Math.max(
        0,
        state.pendingApplications.totalItems - 1
      );
    },
    setPastCurrentPage(state, action: PayloadAction<number>) {
      state.pastApplications.currentPage = action.payload;
    },
    setPastPerPage(state, action: PayloadAction<number>) {
      state.pastApplications.perPage = action.payload;
    },
    clearPastApplications(state) {
      state.pastApplications.applications = [];
      state.pastApplications.totalItems = 0;
      state.pastApplications.error = null;
    },
    setOtherCurrentPage(state, action: PayloadAction<number>) {
      state.otherApplications.currentPage = action.payload;
    },
    setOtherPerPage(state, action: PayloadAction<number>) {
      state.otherApplications.perPage = action.payload;
    },
    clearOtherApplications(state) {
      state.otherApplications.applications = [];
      state.otherApplications.totalItems = 0;
      state.otherApplications.error = null;
    },
  },
  extraReducers: (builder) => {
    // Pending Applications
    builder
      .addCase(fetchPendingApplications.pending, (state) => {
        state.pendingApplications.loading = true;
        state.pendingApplications.error = null;
      })
      .addCase(
        fetchPendingApplications.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.pendingApplications.loading = false;
          const payload = action.payload || {};
          const items = payload.items ?? payload.data ?? [];
          state.pendingApplications.applications = Array.isArray(items)
            ? items
            : [];

          const totalFromPayload =
            payload.total ??
            payload.meta?.total ??
            (Array.isArray(items) ? items.length : 0);
          state.pendingApplications.totalItems = totalFromPayload;

          state.pendingApplications.currentPage =
            payload.page ??
            payload.meta?.pageNumber ??
            state.pendingApplications.currentPage;
          state.pendingApplications.perPage =
            payload.per_page ??
            payload.meta?.per_page ??
            payload.meta?.pageSize ??
            state.pendingApplications.perPage;
          state.pendingApplications.totalPages =
            payload.meta?.totalPages ?? state.pendingApplications.totalPages;
        }
      )
      .addCase(
        fetchPendingApplications.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.pendingApplications.loading = false;
          state.pendingApplications.error =
            action.payload || "Failed to load pending applications";
        }
      );

    // Past Applications
    builder
      .addCase(fetchPastApplications.pending, (state) => {
        state.pastApplications.loading = true;
        state.pastApplications.error = null;
      })
      .addCase(
        fetchPastApplications.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.pastApplications.loading = false;
          const payload = action.payload || {};
          const items = payload.items ?? payload.data ?? [];
          state.pastApplications.applications = Array.isArray(items)
            ? items
            : [];

          const totalFromPayload =
            payload.total ??
            payload.meta?.total ??
            (Array.isArray(items) ? items.length : 0);
          state.pastApplications.totalItems = totalFromPayload;

          state.pastApplications.currentPage =
            payload.page ??
            payload.meta?.pageNumber ??
            state.pastApplications.currentPage;
          state.pastApplications.perPage =
            payload.per_page ??
            payload.meta?.per_page ??
            payload.meta?.pageSize ??
            state.pastApplications.perPage;
          state.pastApplications.totalPages =
            payload.meta?.totalPages ?? state.pastApplications.totalPages;
        }
      )
      .addCase(
        fetchPastApplications.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.pastApplications.loading = false;
          state.pastApplications.error =
            action.payload || "Failed to load past applications";
        }
      );

    // Other Applications
    builder
      .addCase(fetchOtherApplications.pending, (state) => {
        state.otherApplications.loading = true;
        state.otherApplications.error = null;
      })
      .addCase(
        fetchOtherApplications.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.otherApplications.loading = false;
          const payload = action.payload || {};
          const items = payload.items ?? payload.data ?? [];
          state.otherApplications.applications = Array.isArray(items)
            ? items
            : [];

          const totalFromPayload =
            payload.total ??
            payload.meta?.total ??
            (Array.isArray(items) ? items.length : 0);
          state.otherApplications.totalItems = totalFromPayload;

          state.otherApplications.currentPage =
            payload.page ??
            payload.meta?.pageNumber ??
            state.otherApplications.currentPage;
          state.otherApplications.perPage =
            payload.per_page ??
            payload.meta?.per_page ??
            payload.meta?.pageSize ??
            state.otherApplications.perPage;
          state.otherApplications.totalPages =
            payload.meta?.totalPages ?? state.otherApplications.totalPages;
        }
      )
      .addCase(
        fetchOtherApplications.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.otherApplications.loading = false;
          state.otherApplications.error =
            action.payload || "Failed to load other applications";
        }
      );

    // Application Details
    builder
      .addCase(fetchApplicationAllDetails.pending, (state) => {
        state.applicationDetails.loading = true;
        state.applicationDetails.error = null;
      })
      .addCase(
        fetchApplicationAllDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.applicationDetails.loading = false;
          state.applicationDetails.data = action.payload || {};
        }
      )
      .addCase(
        fetchApplicationAllDetails.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.applicationDetails.loading = false;
          state.applicationDetails.error =
            action.payload || "Failed to load application details";
        }
      );

    // Update Application Status
    builder
      .addCase(updateApplicationStatus.pending, (state) => {
        state.applicationDetails.loading = true;
        state.applicationDetails.error = null;
      })
      .addCase(
        updateApplicationStatus.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.applicationDetails.loading = false;
          state.applicationDetails.data = action.payload || {};
        }
      )
      .addCase(
        updateApplicationStatus.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.applicationDetails.loading = false;
          state.applicationDetails.error =
            action.payload || "Failed to update application status";
        }
      );
  },
});

export const {
  setPendingCurrentPage,
  setPendingPerPage,
  clearPendingApplications,
  removePendingApplication,
  setPastCurrentPage,
  setPastPerPage,
  clearPastApplications,
  setOtherCurrentPage,
  setOtherPerPage,
  clearOtherApplications,
} = tuitionManagerPortalSlice.actions;

export const tuitionManagerPortalReducer = tuitionManagerPortalSlice.reducer;
